import { FormEvent, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ROUTES } from '../lib/constants'
import { establishRecoverySession } from '../lib/authRecovery'
import { supabase } from '../lib/supabase'

import { validatePasswordForSignup } from './Login/handlers'
import { GenufitAuthBrand } from './GenufitAuthBrand'

const PASSWORD_VALID_CLASS = 'text-brand-primary'
const INPUT_CLASS =
  'mt-1 block w-full px-3 py-2 border border-border-strong rounded-md bg-surface-default text-small text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:border-interactive'

function PasswordRulesList({ password }: { password: string }) {
  const rules = [
    { label: 'At least 8 characters', satisfied: password.length >= 8 },
    { label: 'One uppercase letter', satisfied: /[A-Z]/.test(password) },
    { label: 'One lowercase letter', satisfied: /[a-z]/.test(password) },
    { label: 'One number', satisfied: /\d/.test(password) },
    {
      label: 'One special character',
      satisfied: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    },
  ]

  return (
    <ul className="mt-4 space-y-1 text-small text-text-muted" data-testid="password-rules-list" aria-label="Password requirements">
      {rules.map((rule) => (
        <li key={rule.label} className={rule.satisfied ? PASSWORD_VALID_CLASS : undefined}>
          {rule.satisfied ? '✓ ' : '• '}
          {rule.label}
        </li>
      ))}
    </ul>
  )
}

function UpdatePassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    const init = async () => {
      try {
        await establishRecoverySession(searchParams)
        if (!cancelled) setReady(true)
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not open password reset.')
        }
      }
    }
    init()
    return () => {
      cancelled = true
    }
  }, [searchParams])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!validatePasswordForSignup(password, setError)) return
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password })
      if (updateError) throw updateError
      navigate(ROUTES.LOGIN, {
        replace: true,
        state: { message: 'Password updated. Sign in with your new password.' },
      })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update password.')
    } finally {
      setLoading(false)
    }
  }

  if (!ready && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-subtle px-4">
        <p className="text-body text-text-secondary">Preparing password reset…</p>
      </div>
    )
  }

  if (!ready && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-subtle px-4">
        <div className="flex w-full max-w-[400px] flex-col items-center gap-8">
          <GenufitAuthBrand />
          <div className="w-full rounded-lg bg-surface-default p-8 shadow-sm space-y-4">
            <div className="bg-danger-subtle border border-danger/20 text-danger-strong px-4 py-3 rounded-md text-small">
              {error}
            </div>
            <button
              type="button"
              onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
              className="w-full min-h-11 px-4 py-2.5 text-small font-medium text-text-primary bg-surface-muted rounded-md hover:bg-surface-subtle transition-colors"
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-subtle px-4" data-testid="update-password-page">
      <div className="flex w-full max-w-[400px] flex-col items-center gap-8">
        <GenufitAuthBrand />

        <div className="w-full rounded-lg bg-surface-default p-8 shadow-sm">
          <h2 className="text-center text-h2 font-semibold text-text-primary mb-6">Set a new password</h2>

          <form className="space-y-4" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-danger-subtle border border-danger/20 text-danger-strong px-4 py-3 rounded-md text-small">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="new-password" className="block text-small font-medium text-text-secondary">
                New password
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label htmlFor="confirm-new-password" className="block text-small font-medium text-text-secondary">
                Confirm password
              </label>
              <input
                id="confirm-new-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full min-h-11 py-2 px-4 border border-transparent text-small font-medium rounded-md text-text-inverse bg-brand-primary hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-interactive-focus-ring disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Update password'}
            </button>
            <PasswordRulesList password={password} />
          </form>
        </div>
      </div>
    </div>
  )
}

export default UpdatePassword
